import { Button, Checkbox, Input, Modal, Tooltip } from "antd";
import { groupBy } from "lodash";
import { useMemo, useRef, useState } from "react";
import { BsFillSendFill } from "react-icons/bs";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import { conversationType } from "../../config/Constant";
import { useMailContextV2 } from "../../context/MailContextV2";
import { useMenuContext } from "../../context/MenuContext";
import AddMemberList from "../../pages/conversation/mail/AddMemberList";
import { useInfoUser } from "../../store/UserStore";
import {
  getFirstLineFromHtml,
  getPlainTextFromHtml,
  openNotificationError,
} from "../../utils/Utils";
import FileHandlerWrapper from "../mail/FileHandlerWrapper";
import { MailFilesPreview } from "../preview/MailFilesPreview";
import EmailComposer from "../richTextEditor/EmailComposer";
import { AddMemberModal } from "./AddMemberModal";
import { PreviewFileUploadModal } from "./PreviewFileUploadModal";
import "./style.scss";

const ForwardAllMailModal = ({ addMemberOrg }) => {
  const { languageMap, user } = useInfoUser();
  const { openAddMemberModal, handleChangeConversation, selectedConversation } =
    useMenuContext();
  const {
    isForwardAllMail,
    setIsForwardAllMail,
    memberOrgList,
    setMemberOrgList,
    setIsLoading,
    isLoading,
  } = useMailContextV2();

  const [isNotification, setIsNotification] = useState(false);

  const [uploadFiles, setUploadFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  const replyDivRef = useRef(null);
  const [mailContent, setMailContent] = useState({
    content: "",
    oldContent: "",
  });
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [forwardTitle, setForwardTitle] = useState("");

  const memberGroupList = useMemo(() => {
    return Object.values(
      groupBy(
        memberOrgList?.filter((org) => org?.type === "PERSON" && org?.checked),
        "objectId",
      ),
    );
  }, [memberOrgList]);

  const validateSendMail = () => {
    if (
      mailContent?.content?.trim()?.length === 0 &&
      previewFiles?.length === 0
    ) {
      openNotificationError(
        languageMap?.["error.mailHaveContent"] ??
          "You have to input email content",
      );

      setIsLoading(false);
      return false;
    }

    return true;
  };

  const createNotification = async () => {
    setIsLoading(true);

    if (!validateSendMail()) return;
    const { userIds, ccIds } = addMemberOrg();

    if (!userIds || userIds?.length === 0) {
      openNotificationError(
        languageMap
          ? languageMap["error.chosenRecipient"]
          : "You haven't chosen recipient",
      );

      setIsLoading(false);
      return;
    }

    const result = await apiFactory.conversationApi.createMailGroup({
      name: forwardTitle || getFirstLineFromHtml(mailContent?.content),
      userIds,
      ccIds,
      conversationType: conversationType.NOTIFICATION,
      forwardConversationId: selectedConversation?.conversationId,
      isNotification: isNotification,
    });

    if (result?.status !== 200) {
      openNotificationError(languageMap?.[result?.message]);
    } else {
      const conversationRes = result?.data;
      conversationRes.version = 2;
      await sendMail(conversationRes?.conversationId);
      handleChangeConversation(conversationRes);
      setIsForwardAllMail(false);
    }

    setIsLoading(false);
  };

  const sendMail = async (creatingConversationId) => {
    if (!validateSendMail()) return;
    const { userIds, ccIds } = addMemberOrg();

    const rs = await apiFactory.mailApi.sendMessage({
      requestUuid: uuidv4(),
      conversationId: creatingConversationId,
      recipientIds: userIds,
      ccIds: ccIds,
      content: mailContent?.content,
      isDraft: false,
      files: previewFiles || [],
      updatedMediaConversationId: true,
      plainText: getPlainTextFromHtml(mailContent?.content),
      isForwardAllMail: true,
    });

    if (rs && rs.status !== 200) {
      openNotificationError("Create mail unsuccessfully");
    }
  };

  const handleCancelUpload = () => {
    setIsOpenUploadModal(false);
    setUploadFiles([]);
  };

  const customHeader = (
    <div className="custom-modal-header flex items-center justify-between pt-5">
      <span className="text-lg font-bold">
        {languageMap?.["chat.mail.title.forwardAll"] ?? "Forward all messages"}
      </span>
      <Tooltip
        placement="bottom"
        title={
          languageMap?.["modal.notification.notice"] ??
          "When you want to notify to a lot of people"
        }
        color={"#0091ff"}
      >
        <Checkbox
          checked={isNotification}
          onChange={() => setIsNotification(!isNotification)}
          className="font-semibold whitespace-nowrap"
        >
          {languageMap?.["notification.notice"] ?? "Notice"}
        </Checkbox>
      </Tooltip>
    </div>
  );

  return (
    <Modal
      title={customHeader}
      closable={{ "aria-label": "Custom Close Button" }}
      open={isForwardAllMail}
      width={"80vw"}
      onOk={() => setIsForwardAllMail(false)}
      onCancel={() => setIsForwardAllMail(false)}
      centered
      footer={false}
    >
      <div className="card-detail">
        <AddMemberList
          title={languageMap?.["chat.mail.recipient"] ?? "Recipient"}
          onClick={openAddMemberModal}
          memberGroupList={memberGroupList}
          orgList={memberOrgList}
          setOrgList={setMemberOrgList}
        />
      </div>
      <div className="flex justify-between mt-[10px] w-full">
        <div className="w-[25%]">
          <AddMemberModal
            isModalOpen={true}
            screen={conversationType.CREATE_NOTIFICATION}
            orgList={memberOrgList}
            setOrgList={setMemberOrgList}
            isForwardMail={isForwardAllMail}
          />
        </div>
        <div className="w-[75%]">
          <div className="flex flex-col gap-2 w-full py-1 border border-gray-300 transition-all">
            <Input
              className="border rounded p-1 px-3 w-full"
              placeholder={
                languageMap?.["modal.notification.title"] ?? "Title(not blank)"
              }
              maxLength={255}
              variant="borderless"
              value={forwardTitle}
              onChange={(e) => {
                setForwardTitle(e.target.value);
              }}
              allowClear
            />
          </div>
          <FileHandlerWrapper
            setUploadFiles={setUploadFiles}
            setIsOpenUploadModal={setIsOpenUploadModal}
            languageMap={languageMap}
            innerRef={replyDivRef}
          >
            <div className="w-full h-[450px] bg-white rounded-[10px] mb-1">
              <div className="border-black border-radius pt-2 pb-2">
                <EmailComposer
                  originalContent=""
                  setMailContent={setMailContent}
                  mailContent={mailContent}
                  conversationId={selectedConversation?.conversationId}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex gap-[10px]">
                <MailFilesPreview
                  previewFiles={previewFiles}
                  setPreviewFiles={setPreviewFiles}
                  setIsOpenUploadModal={setIsOpenUploadModal}
                  setUploadFiles={setUploadFiles}
                />
              </div>
              <div className="flex justify-end mt-[20px]">
                <Button
                  className="bg-[#2a56b9] text-[white]"
                  onClick={createNotification}
                  disabled={isLoading}
                  icon={
                    <BsFillSendFill className="text-[18px]" color="#30a3e6" />
                  }
                >
                  {languageMap?.["chat.mail.btn.forward"] ?? "Forward"}
                </Button>
              </div>
            </div>
          </FileHandlerWrapper>
        </div>
      </div>
      {isOpenUploadModal && (
        <PreviewFileUploadModal
          isModalOpen={isOpenUploadModal}
          handleCancel={handleCancelUpload}
          uploadFiles={uploadFiles}
          setUploadFiles={setUploadFiles}
          previewFiles={previewFiles}
          setPreviewFiles={setPreviewFiles}
        />
      )}
    </Modal>
  );
};

export { ForwardAllMailModal };
